import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";

const BASE_URL = import.meta?.env?.VITE_BASE_URL || "https://nissi-india.com";

const SupervisorPaymentReportAllPrint = () => {
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ✅ read params from URL (must match what you sent in handleAllPrint)
  const qs = useMemo(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return {
      fromDate: urlParams.get("fromDate") || "",          // YYYY-MM-DD
      toDate: urlParams.get("toDate") || "",              // YYYY-MM-DD
      supervisorName: urlParams.get("supervisorName") || "", // supervisorid
      supervisorCode: urlParams.get("supervisorCode") || "", // supervisorid
      entryNumber: urlParams.get("entryNumber") || "",    // Payment/7
      branchid: urlParams.get("branchid") || "",          // 8
    };
  }, []);

  const fetchReportData = async () => {
    setLoading(true);
    setError(null);

    try {
      if (!qs.branchid) throw new Error("branchid missing in URL");
      if (!qs.fromDate || !qs.toDate) throw new Error("fromDate/toDate missing in URL");

      const res = await axios.get(`${BASE_URL}/api/supervisorReport/search`, {
        params: {
          fromDate: qs.fromDate,
          toDate: qs.toDate,
          supervisorName: qs.supervisorName,
          supervisorCode: qs.supervisorCode,
          entryNumber: qs.entryNumber,
          branchid: qs.branchid,
        },
      });

      // ✅ normalize backend recordset to your table format
      const rows = (res.data?.data || []).map((r, i) => ({
        sno: i + 1,
        date: r.fld_date ?? r.date ?? r.entrydate,
        supervisorCode: r.fld_supervisorCode || r.supervisorCode || "",
        supervisorName: r.fld_supervisorname || r.supervisorName || "",
        amount: Number(r.fld_amount ?? r.amount ?? 0),
        amountInWords: r.amountInWords || r.fld_amountinwords || "",
        branch: r.fld_branchname || r.branchName || "",
        paymentid: r.fld_paymentid ?? r.paymentid, // optional
      }));

      setReportData(rows);

      // ✅ auto print
      setTimeout(() => window.print(), 300);
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.message || err.message || "Failed to load report");
      setReportData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReportData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalAmount = reportData.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);

  return (
    <div className="max-w-full mx-auto p-4 bg-white font-sans text-xs print:p-0 print:max-w-none">
      {/* Header Section */}
      <div className="border-2 border-black mb-0">
        <div className="text-center py-3 border-b border-black">
          <h1 className="text-lg font-bold text-red-600">Nissi Infotech</h1>
        </div>
        <div className="text-center py-2 border-b border-black font-bold">
          Supervisor Payment Report
        </div>
      </div>

      {/* Loading/Error */}
      {loading && (
        <div className="text-center py-4">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
          <div className="mt-2">Loading report data...</div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          <div className="font-bold">Error loading report:</div>
          <div>{error}</div>
          <button
            onClick={fetchReportData}
            className="mt-2 bg-red-600 text-white px-3 py-1 rounded text-xs hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      )}

      {/* Table */}
      <div className="border-l-2 border-r-2 border-b-2 border-black">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-black">
              <th className="border-r border-black px-1 py-2 w-8">Sno</th>
              <th className="border-r border-black px-1 py-2 w-16">Date</th>
              <th className="border-r border-black px-1 py-2 w-20">Supervisor Code</th>
              <th className="border-r border-black px-1 py-2 w-20">Supervisor Name</th>
              <th className="border-r border-black px-1 py-2 w-16">Amount</th>
              <th className="border-r border-black px-1 py-2 w-32">Amount In Words</th>
              <th className="border-r border-black px-1 py-2 w-16">Branch</th>
            </tr>
          </thead>

          <tbody>
            {!loading && reportData.length === 0 ? (
              <tr>
                <td colSpan="7" className="p-4 text-center text-gray-500 border-r border-black">
                  No data available for the selected criteria
                </td>
              </tr>
            ) : (
              reportData.map((item, index) => (
                <tr key={index} className="border-b border-gray-300">
                  <td className="border-r border-black px-1 py-1 text-center">{item.sno}</td>
                  <td className="border-r border-black px-1 py-1 text-center">{item.date}</td>
                  <td className="border-r border-black px-1 py-1 text-center">{item.supervisorCode}</td>
                  <td className="border-r border-black px-1 py-1">{item.supervisorName}</td>
                  <td className="border-r border-black px-1 py-1 text-right">
                    {Number(item.amount || 0).toLocaleString("en-IN", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </td>
                  <td className="border-r border-black px-1 py-1">{item.amountInWords}</td>
                  <td className="border-r border-black px-1 py-1 text-center">{item.branch}</td>
                </tr>
              ))
            )}

            {/* Total */}
            {reportData.length > 0 && (
              <tr className="border-t-2 border-black font-bold">
                <td colSpan="4" className="border-r border-black px-1 py-1 text-right"></td>
                <td className="border-r border-black px-1 py-1 text-right">
                  {totalAmount.toLocaleString("en-IN", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </td>
                <td colSpan="2" className="px-1 py-1"></td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="text-right mt-2 text-xs">1</div>

      <div className="mt-4 text-xs text-gray-600 border-t border-gray-300 pt-2">
        <div className="underline">
          Report generated by ERP-Software - www.infraconsoft.com owned by www.nissiinfotech.com
        </div>
      </div>

      <style>{`
        @media print {
          body { margin: 0; }
          * { -webkit-print-color-adjust: exact; }
        }
      `}</style>
    </div>
  );
};

export default SupervisorPaymentReportAllPrint;
